import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { api } from "../api/client";
import { Alert, ICON } from "../components/ui";
import { post, Section, useAdmin, when } from "./ui";

// „Configurare cont” (owner 2026-11-04): what one client gets beyond (or instead of) its package — the package set
// by hand, AI modules, nutrition analyses, the banner, limits, the AI cap, a preferential price or a discount.
// An empty field keeps the package's default.

type Overrides = Record<string, number | string | null>;

const TRI: [string, string][] = [
  ["", "Ca în pachet"],
  ["1", "Inclus"],
  ["0", "Oprit"],
];

/** Fields kept as typed text in the form, with how they map onto the API's integers. */
const NUMBERS: { key: string; label: string; hint: string; scale: number }[] = [
  { key: "nutrition_analyses", label: "Analize nutriționale pe an", hint: "Pro: 1.000", scale: 1 },
  { key: "nutrition_labels", label: "dintre care etichete/poze", hint: "Pro: 300", scale: 1 },
  {
    key: "ai_cap_micro",
    label: "Limită cheltuieli AI pe an (USD)",
    hint: "Standard 4 · Pro 8 · max 20",
    scale: 1e6,
  },
  {
    key: "locations_included",
    label: "Locații incluse fără plată",
    hint: "pe lângă cea principală",
    scale: 1,
  },
  { key: "languages_included", label: "Limbi incluse fără plată", hint: "pe lângă cea inclusă", scale: 1 },
  { key: "products_limit", label: "Produse (plan gratuit)", hint: "implicit 20", scale: 1 },
  { key: "scans_limit", label: "Scanări pe lună (plan gratuit)", hint: "implicit 1.000", scale: 1 },
  { key: "price_month_minor", label: "Preț lunar € (fără TVA)", hint: "pachet: 14,90 / 24,90", scale: 100 },
  { key: "price_year_minor", label: "Preț anual € (fără TVA)", hint: "pachet: 149 / 229", scale: 100 },
  {
    key: "price_month_ron_minor",
    label: "Preț lunar lei (fără TVA)",
    hint: "pentru firmele din România",
    scale: 100,
  },
  {
    key: "price_year_ron_minor",
    label: "Preț anual lei (fără TVA)",
    hint: "pentru firmele din România",
    scale: 100,
  },
  { key: "discount_percent", label: "Reducere permanentă %", hint: "fără preț preferențial", scale: 1 },
];
const FLAGS = ["ai_import", "ai_translate", "nutrition", "hide_banner"] as const;

const toForm = (o: Overrides | null) => {
  const f: Record<string, string> = { note: String(o?.note ?? "") };
  for (const k of FLAGS) f[k] = o?.[k] === null || o?.[k] === undefined ? "" : String(o[k]);
  for (const n of NUMBERS) {
    const v = o?.[n.key];
    f[n.key] = v === null || v === undefined ? "" : String(Number(v) / n.scale).replace(".", ",");
  }
  return f;
};

const PLANS: [string, string][] = [
  ["standard_year", "Standard anual"],
  ["standard_month", "Standard lunar"],
  ["pro_year", "Pro anual"],
  ["pro_month", "Pro lunar"],
  ["trial", "Probă gratuită (Standard)"],
  ["free", "Gratuit"],
];

export function AccountConfig({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useAdmin<{ overrides: Overrides | null }>(["overrides", id], `/restaurants/${id}/overrides`);
  const [form, setForm] = useState<Record<string, string>>(toForm(null));
  const [plan, setPlan] = useState({ planId: "pro_year", until: "" });
  const [msg, setMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  useEffect(() => {
    if (q.data) setForm(toForm(q.data.overrides));
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, number | string | null> = { note: form.note?.trim() || null };
      for (const k of FLAGS) body[k] = form[k] === "" ? null : Number(form[k]);
      for (const n of NUMBERS) {
        const raw = (form[n.key] ?? "").trim().replace(/\s/g, "").replace(",", ".");
        if (raw === "") body[n.key] = null;
        else {
          const v = Math.round(Number(raw) * n.scale);
          if (!Number.isFinite(v) || v < 0) throw new Error(`„${n.label}” nu este un număr valid.`);
          body[n.key] = v;
        }
      }
      return api<{ overrides: Overrides | null }>("PUT", `/admin/restaurants/${id}/overrides`, body);
    },
    onSuccess: () => {
      setMsg({
        tone: "success",
        text: "Configurarea a fost salvată. Se aplică imediat, inclusiv în meniul public.",
      });
      return qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (e) => setMsg({ tone: "error", text: e instanceof Error ? e.message : String(e) }),
  });
  const setPackage = useMutation({
    mutationFn: () =>
      post(`/restaurants/${id}/plan`, {
        planId: plan.planId,
        until:
          plan.planId === "free" ? null : plan.until ? new Date(`${plan.until}T23:59:59`).getTime() : null,
      }),
    onSuccess: () => {
      setMsg({ tone: "success", text: "Pachetul a fost setat, fără plată prin Stripe." });
      return qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (e) => setMsg({ tone: "error", text: e instanceof Error ? e.message : String(e) }),
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const updated = q.data?.overrides?.updated_at;

  return (
    <Section
      title="Configurare cont"
      actions={
        <span className="caption muted">
          {updated ? `Modificată ${when(updated)}` : "Câmp gol = ca în pachet"}
        </span>
      }
    >
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}

      <form
        className="row"
        style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setMsg(null);
          setPackage.mutate();
        }}
      >
        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">Pachet setat de tine</span>
          <select
            className="field-input"
            value={plan.planId}
            onChange={(e) => setPlan((p) => ({ ...p, planId: e.target.value }))}
          >
            {PLANS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        {plan.planId !== "free" && (
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Valabil până pe</span>
            <input
              className="field-input"
              type="date"
              value={plan.until}
              onChange={(e) => setPlan((p) => ({ ...p, until: e.target.value }))}
            />
          </label>
        )}
        <button type="submit" className="btn btn-secondary btn-sm" disabled={setPackage.isPending}>
          Setează pachetul
        </button>
        <span className="caption muted" style={{ flex: "1 1 260px" }}>
          Pentru plată prin transfer sau un cadou. Nu se poate cât clientul plătește prin Stripe.
        </span>
      </form>

      <form
        className="stack"
        style={{ gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setMsg(null);
          save.mutate();
        }}
      >
        <div className="adm-config-grid">
          {(
            [
              ["ai_import", "Import AI din meniu"],
              ["ai_translate", "Traduceri automate"],
              ["nutrition", "Valori nutriționale"],
            ] as const
          ).map(([k, l]) => (
            <label key={k} className="stack" style={{ gap: 4 }}>
              <span className="field-label">{l}</span>
              <select className="field-input" value={form[k]} onChange={(e) => set(k, e.target.value)}>
                {TRI.map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Banda „Meniu creat cu Meniova”</span>
            <select
              className="field-input"
              value={form.hide_banner}
              onChange={(e) => set("hide_banner", e.target.value)}
            >
              <option value="">Ca în pachet</option>
              <option value="1">Ascunsă</option>
            </select>
          </label>
          {NUMBERS.map((n) => (
            <label key={n.key} className="stack" style={{ gap: 4 }}>
              <span className="field-label">{n.label}</span>
              <input
                className="field-input"
                inputMode="decimal"
                value={form[n.key]}
                placeholder={n.hint}
                onChange={(e) => set(n.key, e.target.value)}
              />
            </label>
          ))}
        </div>
        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">Ce i-ai promis clientului (notă internă)</span>
          <textarea
            className="field-input"
            rows={2}
            maxLength={2000}
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
            placeholder="Ex.: grup cu 3 restaurante, Pro 179 €/an + 50 € pe fiecare restaurant nou."
          />
        </label>
        <p className="caption muted" style={{ margin: 0 }}>
          Prețul preferențial se aplică la următoarea plată pornită de client (înlocuiește prețul pachetului
          și reducerea din primul an). Pe planul gratuit, modulele AI pornite au nevoie și de o limită AI.
        </p>
        <div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending || q.isLoading}>
            <SlidersHorizontal {...ICON} aria-hidden="true" />
            Salvează configurarea
          </button>
        </div>
      </form>
    </Section>
  );
}
